<template>
    <div class="container-fluid page">
<!--        <div class="page-subtitle">Hi {{ user.name }}</div>-->

        <div v-if="loading.reports" class="loading-big mt-5"></div>

        <template v-else>
            <div v-if="loadError" class="empty-text">
                Unable to load reports. <a href="#" @click.prevent="loadReports">Try again</a>
            </div>
            <div v-else-if="reports.length === 0" class="empty-text">
                No reports available.
            </div>
            <template v-else>
                <div v-if="reports.length > 1" class="mb-4 d-flex flex-wrap gap-2">
                    <button
                        v-for="report in reports"
                        :key="report.portalReportID"
                        class="btn btn-outline-secondary"
                        :class="{'active': selectedReportID === report.portalReportID}"
                        @click="selectReport(report)"
                    >
                        {{ report.reportTitle || report.reportName }}
                    </button>
                </div>

                <div v-if="currentState.loading" class="loading-big mt-5"></div>

                <div v-else-if="currentState.error" class="empty-text">
                    Unable to load report. <a href="#" @click.prevent="selectReport({portalReportID: selectedReportID})">Try again</a>
                </div>

                <!-- One persistent container per report. v-show keeps each embed
                     alive in the DOM so switching back never reloads it. -->
                <div
                    v-for="report in reports"
                    :key="report.portalReportID"
                    v-show="selectedReportID === report.portalReportID"
                    :ref="'report-' + report.portalReportID"
                    class="report-container"
                    :style="reportHeights[report.portalReportID] ? {height: reportHeights[report.portalReportID] + 'px'} : null"
                ></div>
            </template>
        </template>
    </div>
</template>

<script>
import { mapState } from 'vuex'
import * as pbi from 'powerbi-client'
import {models} from "powerbi-client";

export default {
    name: 'Reports',
    data () {
        return {
            loading: {
                // Starts true so the first render shows the spinner, not "No reports available"
                reports: true,
            },
            loadError: false,
            reports: [],
            selectedReportID: null,
            // Per-report state keyed by portalReportID: { loading, error, embedded }
            reportState: {},
            // Container height in px per report, sized to the page so the iframe never scrolls
            reportHeights: {},
            powerbiService: null,
            // Session the current list was loaded with. Layout keeps this component alive
            // across logout/login, so a different session means the cached data is stale.
            loadedSessionID: null,
        }
    },
    computed: {
        ...mapState({
            user: state => state.user,
        }),
        currentState () {
            return this.reportState[this.selectedReportID] || {}
        },
    },
    mounted () {
        this.powerbiService = new pbi.service.Service(
            pbi.factories.hpmFactory,
            pbi.factories.wpmpFactory,
            pbi.factories.routerFactory
        )
    },
    created () {
        // Non-reactive: { [portalReportID]: { width, height, navEnabled } } of the active page
        this.pageSizes = {}
        this.onResize = () => this.updateHeight(this.selectedReportID)
    },
    // Runs on first mount and every time the page is revisited (keep-alive)
    activated () {
        window.addEventListener('resize', this.onResize)
        const session = this.$auth.getSession()
        const sessionID = session ? session.sessionID : null
        if (sessionID !== this.loadedSessionID || this.loadError) {
            this.loadReports()
        } else {
            this.$nextTick(this.onResize)
        }
    },
    deactivated () {
        window.removeEventListener('resize', this.onResize)
    },
    beforeDestroy () {
        window.removeEventListener('resize', this.onResize)
    },
    methods: {
        getContainer (id) {
            const refEl = this.$refs['report-' + id]
            return Array.isArray(refEl) ? refEl[0] : refEl
        },
        resetReports () {
            Object.keys(this.reportState).forEach(id => {
                const container = this.getContainer(id)
                if (container) this.powerbiService.reset(container)
            })
            this.reports = []
            this.reportState = {}
            this.reportHeights = {}
            this.pageSizes = {}
            this.selectedReportID = null
        },
        setPageSize (id, size, navEnabled) {
            // Pages with "dynamic" size have no fixed dimensions; keep the CSS default height
            if (!size || !size.width || !size.height) return
            this.pageSizes[id] = { width: size.width, height: size.height, navEnabled }
            this.updateHeight(id)
        },
        updateHeight (id) {
            const size = this.pageSizes[id]
            const container = this.getContainer(id)
            // Hidden containers (v-show) have no width; they're sized when selected
            if (!size || !container || !container.clientWidth) return
            // FitToWidth scales the page to the container width, so the canvas height
            // follows the page's aspect ratio. Extra room for the page tabs at the bottom.
            const chrome = size.navEnabled ? 40 : 8
            const height = Math.ceil(container.clientWidth * size.height / size.width) + chrome
            this.$set(this.reportHeights, id, height)
        },
        loadReports () {
            const session = this.$auth.getSession()
            const sessionID = session ? session.sessionID : null
            this.loadedSessionID = sessionID
            this.resetReports()
            this.loadError = false
            this.loading.reports = true
            this.$http.get('/UserGetReports', {
                params: {
                    // UserType: this.user.userType,
                    // userID: this.user.id,
                },
                noMessage: true,
            }).then(res => {
                // A newer load (e.g. after re-login) owns the state now
                if (sessionID !== this.loadedSessionID) return
                this.loading.reports = false
                if (res.body.errorNumber) {
                    this.loadError = true
                    return
                }
                this.reports = res.body.apiPortalReportList || []
                if (this.reports.length > 0) {
                    this.selectReport(this.reports[0])
                }
            }).catch(() => {
                if (sessionID !== this.loadedSessionID) return
                this.loading.reports = false
                this.loadError = true
            })
        },
        selectReport (report) {
            const id = report.portalReportID
            this.selectedReportID = id

            // Already embedded → v-show reveals the live container, no reload.
            if (this.reportState[id] && this.reportState[id].embedded) {
                this.$nextTick(() => this.updateHeight(id))
                return
            }

            this.$set(this.reportState, id, { loading: true, error: false, embedded: false })
            this.$http.get('/UserGetReport', {
                params: {
                    portalReportID: id,
                    // userID: this.user.id,
                    userRole: '',
                },
                noMessage: true,
            }).then(res => {
                const state = this.reportState[id]
                state.loading = false
                if (!res.body.embedURL || !res.body.embedToken) {
                    state.error = true
                    return
                }
                this.$nextTick(() => {
                    this.embedReport(id, res.body)
                })
            }).catch(() => {
                const state = this.reportState[id]
                if (state) {
                    state.loading = false
                    state.error = true
                }
            })
        },
        embedReport (id, data) {
            const container = this.getContainer(id)
            if (!container) return

            const config = {
                type: 'report',
                tokenType: pbi.models.TokenType.Embed,
                accessToken: data.embedToken,
                embedUrl: data.embedURL,
                permissions: pbi.models.Permissions.All,
                settings: {
                    navContentPaneEnabled: data.reportConfigPageNavigation,
                    filterPaneEnabled: data.reportConfigFilters,
                    background: models.BackgroundType.Default,
                    layoutType: models.LayoutType.Custom,
                    customLayout: {
                        displayOption: models.DisplayOption.FitToWidth,
                    },
                }
            }
            if (data.reportConfigBookmarks) {
                if (!config.settings.panes) config.settings.panes = {}
                config.settings.panes.bookmarks = {visible: true}
            }
            if (data.reportConfigFilters) {
                if (!config.settings.panes) config.settings.panes = {}
                config.settings.panes.filters = {
                    expanded: false,
                    visible: true
                }
            }
            config.settings.background = models.BackgroundType.Default
            console.log({config});
            const report = this.powerbiService.embed(container, config)
            report.off('loaded')
            report.off('rendered')
            report.off('pageChanged')
            const navEnabled = !!data.reportConfigPageNavigation
            report.on('loaded', () => {
                report.getActivePage()
                    .then(page => this.setPageSize(id, page.defaultSize, navEnabled))
                    .catch(() => {})
            })
            // Pages in one report can have different sizes
            report.on('pageChanged', event => {
                const page = event.detail && event.detail.newPage
                if (page) this.setPageSize(id, page.defaultSize, navEnabled)
            })
            report.on('error', () => {
                // const state = this.reportState[id]
                // if (state) state.error = true
            })
            this.reportState[id].embedded = true
        },
    },
}
</script>

<style scoped>
/* Default until the report's page size is known; then an inline height matches the page */
.report-container {
    width: 100%;
    height: 750px;
}
</style>
